import { useBoundStore } from "./components/canvas/stores/useAppStore";
import { ModelItemNameEditJS, UserIdentification } from "target_wasm";
import type {
  CanvasFocusType,
  FocusedCell,
  SelectedRow,
} from "components/canvas/IFocus";
import type { CanvasEditType } from "components/canvas/stagedEdit/StagedEditType";
import {
  TaskType,
  ToWorkerMessage,
  type ExportClipboardResult,
} from "Worker/entities";
import { requestFromWorker } from "Worker/workerRequest";
import { showNotificationGlobal } from "./stores/useNotificationStore";
import { useOrgContextStore } from "./components/main/stores/orgContextStore";
import {
  resolveDatasetMember,
  resolveDatasetMemberFailureAnonymous,
} from "components/canvas/utils/datasetImportBatch";
import { writeClipboard } from "./components/canvas/utils/clipboard";
import { formatBytes } from "components/canvas/detailsSide/utils/formatBytes";
import { useLeftPanelStore } from "components/canvas/leftSide/useLeftPanelStore";
import { importTableIntoModel } from "components/canvas/importTableIntoModel";
import {
  closeRowGraph,
  reexpandRowGraph,
} from "components/canvas/rowGraphRequests";
import { settleRestoreAck } from "components/canvas/detailsSide/tableDetails/TableHistory/restoreAcks";
import {
  checkpointDeleteRefusal,
  checkpointDeleteRefused,
} from "components/canvas/detailsSide/tableDetails/TableHistory/utils";
import type { TypeChangeConfirm } from "components/common/TypeChangeConfirmDialog";
import type { CursorMode } from "@/lib/cursorTools";
import { useWorkbookImportStore } from "components/canvas/fileUpload/workbookImportStore";

export function putIntoClipboard(
  data: string,
  mycorrMeta: string | null | undefined,
  item_type: string,
  show_snackbar: boolean,
) {
  writeClipboard(data, mycorrMeta)
    .then(() => {
      if (show_snackbar) {
        showCopySuccessSnackbar(item_type);
      }
    })
    .catch(() => {
      showNotificationGlobal("Failed to copy to clipboard", "error");
    });
}

export function fetchViewForClipboard(
  requestBody: string,
  _isImported: boolean,
  colSettingsJson: string,
  columnNamesJson: string,
  categoryLabelsJson: string,
  attachmentColumnsJson: string,
  mycorrMeta: string | null | undefined,
) {
  // Write the clipboard now, inside the keypress, with the TSV pending: Safari rejects a later write.
  const worker = useBoundStore.getState().worker;
  if (!worker) {
    showNotificationGlobal("Failed to copy — worker not ready", "error");
    return;
  }

  const tsv = requestFromWorker<ExportClipboardResult>(
    worker,
    TaskType.ExportClipboard,
    {
      requestBody,
      colSettingsJson,
      columnNamesJson,
      categoryLabelsJson,
      attachmentColumnsJson,
    },
  ).then((result) => result.tsvString);

  writeClipboard(tsv, mycorrMeta)
    .then(() => showCopySuccessSnackbar("Table View"))
    .catch(() => {
      showNotificationGlobal("Failed to copy to clipboard", "error");
    });
}

/// Every export names its arguments, mirrored in `wasm_client/src/js_imports.rs`.

export function setCanvasFocus(focus: CanvasFocusType) {
  const state = useBoundStore.getState();
  state.setCanvasFocus(focus);
}

export function setSelectedRow(row: SelectedRow | null) {
  useBoundStore.getState().setSelectedRow(row);
}

export function setFocusedCell(cell: FocusedCell | null) {
  useBoundStore.getState().setFocusedCell(cell);
}

export function useSetColumnSelection(columnIds: string[]) {
  const state = useBoundStore.getState();
  state.setCanvasColumnSelection(columnIds);
}

export function useClearColumnSelection() {
  const state = useBoundStore.getState();
  state.setCanvasColumnSelection(null);
}

export function invalidateModelStructure() {
  useBoundStore.getState().bumpModelStructureVersion();
}

export function invalidateDetail() {
  useBoundStore.getState().bumpDetailVersion();
}

export function invalidateTableHistory() {
  const state = useBoundStore.getState();
  state.invalidateTableHistory();
}

export function setCanvasInput(edit: {
  type: string;
  table_id?: string;
  column_id?: string;
  view_id?: string | null;
  item_id?: string;
  row_id?: number;
  initial_text?: string;
  edit_previous?: boolean;
  edit_entity?: string;
  column_data_type?: string;
  is_type_locked?: boolean;
  display_timezone?: string;
  decimal_places?: number;
  link_enabled?: boolean;
  is_computed?: boolean;
  column_name?: string;
  initial_term?: string;
  match_count?: number | null;
  scanning?: boolean;
}) {
  const ENTITY_MAP: Record<string, ModelItemNameEditJS> = {
    TableView: ModelItemNameEditJS.TableView,
    ModelItem: ModelItemNameEditJS.ModelItem,
    Table: ModelItemNameEditJS.Table,
  };
  const state = useBoundStore.getState();
  let canvasEdit: CanvasEditType;
  switch (edit.type) {
    case "ColumnName":
      if (!edit.view_id) {
        console.error("ColumnName edit overlay missing view_id");
        return;
      }
      canvasEdit = {
        type: "ColumnName",
        initial: edit.initial_text ?? "",
        table_id: edit.table_id!,
        column_id: edit.column_id!,
        fast_edit: edit.edit_previous ?? false,
        view_id: edit.view_id,
      };
      break;
    case "CellValue":
      canvasEdit = {
        type: "CellValue",
        initial: edit.initial_text ?? "",
        table_id: edit.table_id!,
        view_id: edit.view_id!,
        column_id: edit.column_id!,
        row_id: BigInt(edit.row_id!),
        fast_edit: edit.edit_previous ?? false,
        column_data_type: edit.column_data_type,
        is_type_locked: edit.is_type_locked,
        display_timezone: edit.display_timezone,
        decimal_places: edit.decimal_places,
        link_enabled: edit.link_enabled,
        is_computed: edit.is_computed,
      };
      break;
    case "ModelItemName":
      canvasEdit = {
        type: "ModelItemName",
        initial: edit.initial_text ?? "",
        item_id: edit.item_id!,
        fast_edit: edit.edit_previous ?? false,
        edit_entity: ENTITY_MAP[edit.edit_entity!],
      };
      break;
    case "ColumnSearch":
      canvasEdit = {
        type: "ColumnSearch",
        view_id: edit.view_id!,
        column_id: edit.column_id!,
        column_name: edit.column_name ?? "",
        match_count: edit.match_count ?? null,
        scanning: edit.scanning ?? false,
        initial: edit.initial_term ?? "",
        fast_edit: false,
      };
      break;
    case "ColumnFormula":
      canvasEdit = {
        type: "ColumnFormula",
        table_id: edit.table_id!,
        view_id: edit.view_id!,
        column_id: edit.column_id!,
        initial: "",
        fast_edit: false,
      };
      break;
    default:
      return;
  }
  state.setCanvasInput(canvasEdit);
}
export function useUpdateActiveUsers(): (users: unknown[]) => void {
  return (users: unknown[]) => {
    const state = useBoundStore.getState();

    try {
      if (!users || !Array.isArray(users)) {
        console.warn(
          "useUpdateActiveUsers received invalid users data:",
          users,
        );
        state.setActiveUsers([]);
        return;
      }

      const userIdentifications: UserIdentification[] = users
        .filter((user) => user != null)
        .map((user: unknown) => {
          return UserIdentification.from_js(user);
        });

      state.setActiveUsers(userIdentifications);
    } catch (error) {
      console.error("Failed to update active users:", error);
    }
  };
}

export function useUpdateTableDeletionState(
  table_id: string,
  hard_deletion: boolean,
  is_scheduled: boolean,
) {
  const state = useBoundStore.getState();
  state.setTableDeletionState(table_id, hard_deletion, is_scheduled);
}

// The values `wasm_client/src/ui_values.rs` sends. The bindings declare a string
// enum only when an export takes one, and these three only reach JS.

/** `CanvasModeJS`. */
export type CanvasMode = "tabular" | "graph" | "rowGraph";

/** `CommitBadgeJS`: a table's commit badge. Keyed by TABLE: two views of one
 *  table share a commit. `empty` is a commit open with nothing staged yet. */
export type CommitStateViz =
  "commit" | "conflict" | "pauseAuto" | "empty" | "hidden";

/** `FocusPatternJS`. A focused cell also sets the column selection, so this is
 *  the only way to tell the two apart here. */
export type CanvasFocusPattern =
  | "singleColumn"
  | "multiColumn"
  | "rowSelection"
  | "singleCell"
  | "multiCell"
  | "noFocus";

export function setCanvasMode(mode: CanvasMode) {
  useBoundStore.getState().setCanvasMode(mode);
}

export function setSelectedRowCount(count: number) {
  useBoundStore.getState().setSelectedRowCount(count);
}

export function setCanvasFocusPattern(pattern: CanvasFocusPattern) {
  useBoundStore.getState().setCanvasFocusPattern(pattern);
}

export function setCanvasCommitState(
  tableId: string,
  state: CommitStateViz,
  typeChangeStaged: boolean,
) {
  useBoundStore
    .getState()
    .setCanvasCommitState(tableId, state, typeChangeStaged);
}

export function setNewRowCount(count: number) {
  useBoundStore.getState().setNewRowCount(count);
}

export function setGraphExternal(tableIds: string[]) {
  useBoundStore.getState().setGraphExternal(tableIds);
}

export function setRowGraph(size: number, headroom: number) {
  useBoundStore.getState().setRowGraph(size, headroom);
}

export function setZoomRange(canZoomIn: boolean, canZoomOut: boolean) {
  useBoundStore.getState().setZoomRange(canZoomIn, canZoomOut);
}

export function setCursorMode(mode: CursorMode) {
  useBoundStore.getState().setCursorMode(mode);
}

export function onSnapshotLoaded() {
  // No-op kept for the WASM FFI contract.
  // This callback is kept as a no-op for the WASM FFI contract.
}

export function useSendClientDataRequest(
  request_id: string,
  serialized_message: string,
  table_id?: string,
) {
  const state = useBoundStore.getState();
  const workerMessage: ToWorkerMessage = {
    task: TaskType.ClientDataRequest,
    data: { request_id, request: serialized_message, table_id },
  };
  state.sendMessageToWorker(workerMessage);
}

function showCopySuccessSnackbar(item: string) {
  showNotificationGlobal(`${item} copied to clipboard`, "success");
}

export function showNotification(kind: {
  type: string;
  table_name?: string;
  source?: string;
  rows?: number;
  cols?: number;
  reason?: string;
  row_count?: number;
  bytes?: number;
  limit?: number;
  ended?: boolean;
  requested?: number;
  free?: number;
  pairs?: number;
  at_least?: boolean;
}) {
  switch (kind.type) {
    case "CastSuccess":
      showNotificationGlobal("Column casted successfully", "success");
      break;
    case "CopyDisabledSealedSpace":
      showNotificationGlobal(
        "Copying is disabled — this model is in a sealed space",
        "warning",
      );
      break;
    case "StorageQuotaExceeded":
      // The optimistic edit has already snapped back; the refresh raises the
      // banner that explains the whole state.
      showNotificationGlobal(
        "Storage is full, so the change was not saved. Free some space or move to a larger plan.",
        "error",
      );
      void useOrgContextStore.getState().refreshUsage();
      break;
    case "RowGraphAccessLost":
      showNotificationGlobal(
        kind.ended
          ? "Investigation closed — its tables are no longer accessible"
          : "Some rows were removed — their table is no longer accessible",
        "warning",
      );
      break;
    case "RowGraphFull":
      showNotificationGlobal(
        kind.free
          ? `Row graph is full — room for ${kind.free} more, not ${kind.requested}`
          : "Row graph is full — clear it or remove rows to add more",
        "warning",
      );
      break;
    case "RowGraphCollapseHeld":
      showNotificationGlobal(
        (kind.rows ?? 0) === 1
          ? "That row was expanded further — collapse its own links first."
          : `Those ${kind.rows} rows were expanded further — collapse their own links first.`,
        "warning",
      );
      break;
    case "RowGraphLinkTooBig": {
      const n = `${kind.at_least ? "over " : ""}${(kind.rows ?? 0).toLocaleString()}`;
      showNotificationGlobal(
        `That link brings in ${n} rows — the graph has room for ${(kind.free ?? 0).toLocaleString()}. Nothing was added.`,
        "warning",
      );
      break;
    }
    case "RowGraphEdgesIncomplete":
      showNotificationGlobal(
        `Too many linked tables to check at once — connections between ${kind.pairs} table pairs are not shown`,
        "warning",
      );
      break;
    case "CastError":
      showNotificationGlobal("Failed to cast column", "error");
      break;
    case "PasteSuccess":
      // The workbook picker reports each of its tables in place.
      if (useWorkbookImportStore.getState().queue.length > 0) break;
      showNotificationGlobal(
        `Table "${kind.table_name}" created successfully`,
        "success",
      );
      break;
    case "TableImportPending":
      // Dataset-drop members roll into the batch toast (single-slot store).
      // `?? ""` mirrors how the batch registers unnamed members.
      if (resolveDatasetMember(kind.table_name ?? "", "deferred")) break;
      showNotificationGlobal(
        `Table "${kind.table_name}" import pending approval`,
      );
      break;
    case "TableImportWaitingForRelease":
      if (resolveDatasetMember(kind.table_name ?? "", "deferred")) break;
      showNotificationGlobal(
        `"${kind.table_name}" import pending — waiting for release`,
      );
      break;
    case "TableUploadSuccess": {
      const actionLabel = kind.source === "FileUpload" ? "created" : "imported";
      showNotificationGlobal(
        `Table "${kind.table_name}" ${actionLabel} successfully`,
        "success",
      );
      break;
    }
    case "TableImportSuccess":
      if (resolveDatasetMember(kind.table_name ?? "", "success")) break;
      showNotificationGlobal(
        `Table "${kind.table_name}" imported successfully`,
        "success",
      );
      break;
    case "TableImportFailure":
      // The failure ACK carries no table identity — attribute it to the
      // active dataset batch, if any.
      if (resolveDatasetMemberFailureAnonymous()) break;
      showNotificationGlobal(`Import failed — ${kind.reason}`, "error");
      break;
    case "TableDetachSuccess":
      showNotificationGlobal(
        `Table "${kind.table_name}" detached successfully`,
        "success",
      );
      break;
    case "TableDetachFailure":
      showNotificationGlobal(
        kind.reason
          ? `Detach failed: ${kind.reason}`
          : "Detach failed — please try again",
        "error",
      );
      break;
    case "OwnershipTransferSuccess":
      showNotificationGlobal(
        `Table "${kind.table_name}" ownership transferred successfully`,
        "success",
      );
      break;
    case "InvalidFilterColumnRemoval":
      showNotificationGlobal(
        "Can't remove a column used in a filter or sort.",
        "error",
      );
      break;
    case "ColumnRemovalDuringCommit":
      showNotificationGlobal(
        "Columns can't be removed in commit mode. Commit or discard your changes first.",
        "error",
      );
      break;
    case "ColumnRenameDuringCommit":
      showNotificationGlobal(
        "Columns can't be renamed in commit mode. Commit or discard your changes first.",
        "error",
      );
      break;
    case "RemovalFailed":
      showNotificationGlobal(
        "Failed to remove: recently updated by another user",
        "error",
      );
      break;
    case "ModelStateResyncing":
      // The one warning that is still running: `progress: 0` is how a caller
      // asks for the spinner, and it holds until Resynced or Stuck replaces it.
      showNotificationGlobal(
        "Out of sync — reloading from server…",
        "warning",
        undefined,
        0,
      );
      break;
    case "ModelStateResynced":
      showNotificationGlobal("Back in sync with the server", "success");
      break;
    case "ModelStateStuck":
      showNotificationGlobal(
        "Couldn't get back in sync. Please reload the page.",
        "error",
      );
      break;
    case "CheckpointDeletedError":
      checkpointDeleteRefused();
      showNotificationGlobal(checkpointDeleteRefusal(kind.reason), "error");
      break;
    case "TableFastForwardSuccess":
      showNotificationGlobal("Updated to the latest version", "success");
      break;
    case "TableFastForwardError":
      showNotificationGlobal(
        "Failed to update to the latest version.",
        "error",
      );
      break;
    case "CheckpointRestoreSuccess":
      if (!settleRestoreAck(true))
        showNotificationGlobal("Checkpoint restored successfully", "success");
      break;
    case "CheckpointRestoreError":
      settleRestoreAck(false);
      showNotificationGlobal("Failed to restore checkpoint", "error");
      break;
    case "CopyTooLarge":
      showNotificationGlobal(
        `Selection too large to copy (${kind.rows?.toLocaleString()}×${kind.cols?.toLocaleString()} cells). Select fewer rows or columns.`,
        "error",
      );
      break;
    case "CellValueTooLarge":
      showNotificationGlobal(
        `Value too large for one cell (${formatBytes(kind.bytes ?? 0)}, limit ${formatBytes(kind.limit ?? 0)}).`,
        "error",
      );
      break;
    case "CopyFailed":
      showNotificationGlobal("Failed to copy to clipboard", "error");
      break;
    case "CopyLoadingData":
      showNotificationGlobal("Loading data for copy...");
      break;
    case "DiffAlreadyExists":
      showNotificationGlobal("Diff already exists", "default");
      break;
    case "FilterApplied":
      showNotificationGlobal("Filter applied", "success");
      break;
    case "FilterDeleted":
      showNotificationGlobal("Filter deleted", "success");
      break;
    case "FilterFailed":
      showNotificationGlobal(
        "Failed to apply filter. Please try again.",
        "error",
      );
      break;
    case "CannotCreateView":
      showNotificationGlobal(
        kind.reason
          ? `Cannot create view: ${kind.reason}`
          : "Cannot create view on this table",
        "error",
      );
      break;
    case "ExtractUniqueSuccess": {
      const count = kind.row_count ?? 0;
      const plural = count === 1 ? "value" : "values";
      showNotificationGlobal(
        `Created table "${kind.table_name}" with ${count} unique ${plural}`,
        "success",
      );
      break;
    }
    case "ExtractUniqueError":
      showNotificationGlobal(
        kind.reason || "Failed to extract unique values",
        "error",
      );
      break;
    case "TableHardDeleteSuccess":
      showNotificationGlobal(
        `"${kind.table_name}" permanently deleted`,
        "success",
      );
      break;
    case "TableHardDeleteFailure":
      showNotificationGlobal(
        `Couldn't permanently delete the table — ${kind.reason}.`,
        "error",
      );
      break;
  }
}

export function showConflictDialog(kind: {
  type: string;
  table_name?: string;
  column_name?: string;
}) {
  switch (kind.type) {
    case "Column":
      useBoundStore.getState().setConflictDialog({
        title: "Column Conflict",
        message: `Column "${kind.column_name}" in "${kind.table_name}" was modified by another user. Force remove?`,
      });
      break;
    case "Table":
      useBoundStore.getState().setConflictDialog({
        title: "Table Conflict",
        message: `Table "${kind.table_name}" was modified by another user. Force remove?`,
      });
      break;
  }
}

export function showTypeChangeConfirm(confirm: TypeChangeConfirm) {
  useBoundStore.getState().setTypeChangeConfirm(confirm);
}

// Presence callbacks

export function onFollowStateChanged(followingUserId: string): void {
  useBoundStore
    .getState()
    .setFollowingUserId(followingUserId === "" ? null : followingUserId);
}

export function onSessionAway(_clientId: string): void {}

export function onSessionActive(_clientId: string): void {}

/// Module-level ref to the cursor animation start callback.
/// Set by CursorCanvasContainer; called by WASM when a cursor update arrives.
let _onCursorUpdateCallback: (() => void) | null = null;

export function registerOnCursorUpdate(fn: (() => void) | null): void {
  _onCursorUpdateCallback = fn;
}

export function onCursorUpdate(): void {
  _onCursorUpdateCallback?.();
}

// Viewport Persistence

export function saveViewport(
  model_id: string,
  x: number,
  y: number,
  zoom: number,
) {
  try {
    localStorage.setItem(
      `viewport-${model_id}`,
      JSON.stringify({ x, y, zoom }),
    );
  } catch {
    /* full storage or private browsing */
  }
}

export function getSavedViewport(
  model_id: string,
): { x: number; y: number; zoom: number } | null {
  try {
    const raw = localStorage.getItem(`viewport-${model_id}`);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return typeof p.x === "number" &&
      typeof p.y === "number" &&
      typeof p.zoom === "number"
      ? p
      : null;
  } catch {
    return null;
  }
}

// Parent Model

export function onParentModelUpdate(
  tableId: string,
  parentModelId: string,
  parentModelName: string,
  parentModelColor?: string,
  parentModelIconIndex?: number,
): void {
  useBoundStore
    .getState()
    .setParentModel(
      tableId,
      parentModelId,
      parentModelName,
      parentModelColor,
      parentModelIconIndex,
    );
}

// Table Permissions

export function onTablePermissionsUpdate(): void {
  useBoundStore.getState().bumpPermissionsVersion();
}

export function onModelPermissionsUpdate(): void {
  useBoundStore.getState().bumpPermissionsVersion();
}

// UI panels — called from WASM via UiCallbacks

export function openSearch(): void {
  useLeftPanelStore.getState().openSearch();
}

export interface OverflowCategoryChip {
  label: string;
  color: string | null;
}

export interface OverflowIconInfo {
  text: string;
  truncated: boolean;
  shown_bytes: number;
  full_bytes: number;
  view_id: string;
  row_id: number;
  col_id: string;
  col_name: string;
  icon_screen_x: number;
  icon_screen_y: number;
  cell_screen_x: number;
  cell_screen_y: number;
  cell_width: number;
  categories?: OverflowCategoryChip[];
  is_link: boolean;
  /// Attachment cells: one entry per file, badge + name. Present ⇒ the
  /// popover renders the cell's chips instead of the descriptor JSON.
  attachments?: { badge: string; name: string; is_link: boolean }[];
  /// `list_string` cells: one entry per item. Present ⇒ the popover lists the
  /// items instead of their JSON encoding.
  list_items?: string[];
}

export function setOverflowIcon(icon: OverflowIconInfo | null): void {
  useBoundStore.getState().setOverflowIcon(icon);
}

export interface FormulaPeekInfo {
  table_id: string;
  view_id: string;
  column_id: string;
  column_name: string;
  header_left: number;
  header_top: number;
  header_bottom: number;
  header_width: number;
  can_edit: boolean;
  runtime_error: boolean;
}

export function setFormulaPeek(peek: FormulaPeekInfo | null): void {
  useBoundStore.getState().setFormulaPeek(peek);
}

export { importTableIntoModel as importGraphTable };

export interface RowGraphOverlayLink {
  table_id: string;
  name: string;
  expandable: boolean;
  expanded: boolean;
  other_model: boolean;
  /** The rows of that table the graph shows linked to the focused row. */
  shown: number;
}

export interface RowGraphOverlayInfo {
  focused_node: string;
  focused_label: string;
  focused_table_name: string;
  links: RowGraphOverlayLink[];
}

export function setRowGraphOverlay(overlay: RowGraphOverlayInfo | null): void {
  const store = useBoundStore.getState();
  store.setRowGraphOverlay(overlay);
  // A field's `leadsTo` follows the graph, which is when this is pushed.
  store.bumpRowInspectorVersion();
}

export interface GraphOutsideTable {
  table_id: string;
  name: string;
  /** This model takes imports from this viewer; the table's own say is separate. */
  can_import: boolean;
}

export function setGraphOutsideTable(table: GraphOutsideTable | null): void {
  useBoundStore.getState().setGraphOutsideTable(table);
}

type ContextPart =
  | { part: "links"; tables: string[] }
  | { part: "table_description"; table: string }
  | { part: "model_description"; model: string }
  | { part: "node_labels"; table: string }
  | { part: "all" };

// Something outside model state changed (docs/ContextNotices.md). WASM has
// refetched the links already; these are the parts React holds.
export function contextStale(partJson: string): void {
  const part = JSON.parse(partJson) as ContextPart;
  void Promise.all([
    import("./components/canvas/stores/DescriptionStore"),
    import("./components/canvas/nodeLabels"),
    import("./lib/api/linkCount"),
  ]).then(
    ([{ useDescriptionStore }, { fetchNodeLabels }, { fetchLinkCount }]) => {
      const { clientApp, linkCounts } = useBoundStore.getState();
      const descriptions = useDescriptionStore.getState();
      const shown = clientApp?.rowgraph_table_ids() ?? [];
      let [counted, described, labelled]: string[][] = [[], [], []];
      switch (part.part) {
        case "links":
          counted = part.tables;
          break;
        case "table_description":
          described = [part.table];
          break;
        case "model_description":
          described = [part.model];
          break;
        case "node_labels":
          labelled = [part.table];
          break;
        case "all":
          [counted, described, labelled] = [
            Object.keys(linkCounts),
            Object.keys(descriptions.descriptions),
            shown,
          ];
      }
      const warn = (what: string) => (err: unknown) =>
        console.warn(`${what} refresh failed:`, err);

      counted
        .filter((t) => t in linkCounts)
        .forEach((t) => fetchLinkCount(t).catch(warn("link count")));
      described.forEach((id) =>
        descriptions.reload(id.startsWith("mod-") ? "model" : "table", id),
      );
      const stale = labelled.filter((t) => shown.includes(t));
      if (clientApp && stale.length > 0)
        fetchNodeLabels(clientApp, stale).catch(warn("node label"));
    },
  );
}

export function requestRowGraphClose(): void {
  void closeRowGraph();
}

/** One expansion to run again — the row it ran from, and the table it reached. */
export interface RowGraphReexpand {
  source_node: string;
  table_id: string;
}

// WASM asks for expansions to be run again after a data change.
export function requestRowGraphReexpand(expansions: RowGraphReexpand[]): void {
  if (expansions.length === 0) return;
  void reexpandRowGraph(expansions);
}

export type CellAction =
  { kind: "open_link"; text: string } | { kind: "view_files" };

export interface CellActionIconInfo {
  action: CellAction;
  icon_screen_x: number;
  icon_screen_y: number;
  before_overflow: boolean;
}

export function setCellActionIcon(icon: CellActionIconInfo | null): void {
  useBoundStore.getState().setCellActionIcon(icon);
}

export interface OrnamentHintInfo {
  hint: "graph_expand" | "graph_collapse" | "graph_import";
  screen_x: number;
  screen_y: number;
  width: number;
  height: number;
}

/** Collaborators sharing one spot on a table view, under the pointer. */
export interface PresenceHintInfo {
  people: { name: string; color: string }[];
  screen_x: number;
  screen_y: number;
  width: number;
  height: number;
}

export function setOrnamentHint(
  hint: OrnamentHintInfo | null | undefined,
): void {
  useBoundStore.getState().setOrnamentHint(hint ?? null);
}

export function setPresenceHint(
  hint: PresenceHintInfo | null | undefined,
): void {
  useBoundStore.setState({ presenceHint: hint ?? null });
}

// Link reads and writes for WASM, which fires and forgets: each response comes back through the
// matching `on_link_*`, so its per-table cache (10 min, stale-while-revalidate) stays in sync.

const LINKS_ENDPOINT = "/server/action/links";

function reportLinksError(action: string, err: unknown): void {
  console.error(`linksApi ${action} failed:`, err);
  const reason = err instanceof LinksApiError ? err.reason : null;
  showNotificationGlobal(
    reason ? `Link ${action} failed: ${reason}` : `Link ${action} failed`,
    "error",
  );
}

/// The 403 body from `require_link_authority`, so the toast explains a refusal.
class LinksApiError extends Error {
  reason: string | null;
  constructor(status: number, reason: string | null) {
    super(`HTTP ${status}`);
    this.reason = reason;
  }
}

async function throwLinksApiError(res: Response): Promise<never> {
  let reason: string | null = null;
  try {
    const body = (await res.json()) as { reason?: string };
    reason = typeof body.reason === "string" ? body.reason : null;
  } catch {
    /* non-JSON error body — fall back to the bare status */
  }
  throw new LinksApiError(res.status, reason);
}

// Bump after every `on_link_*` so link readers re-render.
function bumpLinksVersion(): void {
  useBoundStore.getState().bumpLinksVersion();
}

export function linksCreate(requestBody: string): void {
  fetch(LINKS_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: requestBody,
    credentials: "include",
  })
    .then(async (res) => {
      if (!res.ok) {
        await throwLinksApiError(res);
      }
      const linkJson = await res.text();
      const clientApp = useBoundStore.getState().clientApp;
      clientApp?.on_link_created(linkJson);
      bumpLinksVersion();
    })
    .catch((err) => reportLinksError("create", err));
}

export function linksDelete(linkId: string): void {
  fetch(`${LINKS_ENDPOINT}/${encodeURIComponent(linkId)}`, {
    method: "DELETE",
    credentials: "include",
  })
    .then(async (res) => {
      // 204 (success) and 404 (already gone — idempotent) are both fine;
      // either way we evict the cached row locally.
      if (!res.ok && res.status !== 404) {
        await throwLinksApiError(res);
      }
      const clientApp = useBoundStore.getState().clientApp;
      clientApp?.on_link_deleted(linkId);
      bumpLinksVersion();
    })
    .catch((err) => reportLinksError("delete", err));
}

// A fetch that ends without links must still end in WASM, or the table counts
// as in flight forever and no later warm asks again.
function linksFetchFailed(tableIdsCsv: string): void {
  useBoundStore.getState().clientApp?.on_link_fetch_failed(tableIdsCsv);
}

export function linksFetchForTable(tableId: string): void {
  const url = `${LINKS_ENDPOINT}?for_table=${encodeURIComponent(tableId)}`;
  const setStatus = (s: "loaded" | "denied" | "error") =>
    useBoundStore.getState().setLinksFetchStatus(tableId, s);
  fetch(url, { credentials: "include" })
    .then(async (res) => {
      if (res.status === 403) {
        // 403 ≠ "no links": render a denied-access message.
        linksFetchFailed(tableId);
        setStatus("denied");
        bumpLinksVersion();
        console.warn(`linksFetchForTable: 403 for ${tableId}`);
        return;
      }
      if (res.status === 404) {
        // 404 = table gone; treat as "no links" rather than an error.
        linksFetchFailed(tableId);
        setStatus("loaded");
        bumpLinksVersion();
        console.warn(`linksFetchForTable: 404 for ${tableId}`);
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const linksJson = await res.text();
      const clientApp = useBoundStore.getState().clientApp;
      clientApp?.on_link_list_received_for_table(tableId, linksJson);
      setStatus("loaded");
      bumpLinksVersion();
    })
    .catch((err) => {
      // Distinct from "denied" so the section can offer a retry.
      linksFetchFailed(tableId);
      setStatus("error");
      bumpLinksVersion();
      reportLinksError("fetch (table)", err);
    });
}

/// Batched `linksFetchForTable`: up to 100 tables, pre-chunked by WASM.
export function linksFetchForTables(tableIdsCsv: string): void {
  const tableIds = tableIdsCsv.split(",").filter(Boolean);
  const setStatus = (id: string, s: "loaded" | "denied" | "error") =>
    useBoundStore.getState().setLinksFetchStatus(id, s);
  fetch(`${LINKS_ENDPOINT}?for_tables=${encodeURIComponent(tableIdsCsv)}`, {
    credentials: "include",
  })
    .then(async (res) => {
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const responseJson = await res.text();
      useBoundStore
        .getState()
        .clientApp?.on_link_list_received_for_tables(responseJson);
      // A table the server refused is absent from the response's `tables`, so
      // it stays uncached in WASM; mark it denied rather than loaded.
      const covered = new Set(
        (JSON.parse(responseJson) as { tables?: string[] }).tables ?? [],
      );
      tableIds.forEach((id) =>
        setStatus(id, covered.has(id) ? "loaded" : "denied"),
      );
      const refused = tableIds.filter((id) => !covered.has(id));
      if (refused.length > 0) linksFetchFailed(refused.join(","));
      bumpLinksVersion();
    })
    .catch((err) => {
      linksFetchFailed(tableIdsCsv);
      tableIds.forEach((id) => setStatus(id, "error"));
      bumpLinksVersion();
      reportLinksError("fetch (tables)", err);
    });
}
